import React, { FC } from "react";
import { Box, Stack, Typography } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";

import StackContent from "@components/StackContent";
import BaseLink from "@components/Link";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors";
import YouTube from "../YouTube";
import CodeViewer from "../CodeViewer";

interface Props {
  title?: string;
  description?: string;
  ctaUrl?: string;
  ctaLabel?: string;
  assetUrl: string;
  assetType?: string;
  assetData?: string[];
  assetMeta?: any;
  assetPosition?: "left" | "right";
  useTwentyFourStyle?: boolean;
}

const BreakOut: FC<Props> = ({
  title = "How it works",
  description,
  ctaLabel = "Learn more",
  ctaUrl,
  assetUrl,
  assetType = "image",
  assetPosition = "right",
  assetData = [],
  assetMeta = {},
  useTwentyFourStyle = false,
}) => {
  const contentLayout = assetPosition === "right" ? "row" : "row-reverse";
  const textVariant = useTwentyFourStyle ? "body1" : "h6";

  const textStyles = useTwentyFourStyle
    ? {
        color: Colors.HeadingText,
        fontFamily: "Kanit",
        fontWeight: 300,
      }
    : {
        color: Colors.TextPrimary,
      };

  const buttonStyles = useTwentyFourStyle
    ? {
        fontFamily: "Kanit",
      }
    : {};

  return (
    <StackContent>
      <Stack
        direction={{ xs: "column", md: contentLayout }}
        gap={5}
        alignItems="center"
      >
        <Stack spacing={2} sx={{ width: !assetUrl ? "100%" : "unset" }}>
          {title && <Typography variant="h2">{title}</Typography>}
          {description && (
            <Typography
              variant={textVariant}
              component="span"
              sx={{ ...textStyles }}
            >
              <ReactMarkdown
                children={description}
                remarkPlugins={[remarkBreaks]}
              />
            </Typography>
          )}
          {ctaUrl && (
            <BaseLink url={ctaUrl} sx={{ ...buttonStyles }}>
              {ctaLabel}
            </BaseLink>
          )}
        </Stack>
        {assetUrl && assetType === "image" && (
          <Box
            component="img"
            src={assetUrl}
            sx={{ width: "100%", maxWidth: "520px", borderRadius: "15px" }}
          />
        )}
        {assetUrl && assetType === "youtube" && (
          <YouTube url={assetUrl} title={title} />
        )}

        {assetType === "code" && assetData?.length > 0 && (
          <Stack
            sx={{
              maxWidth: {
                xs: "100%",
                sm: "520px",
              },
              width: "100%",
              gap: "16px",
            }}
          >
            {assetData.map((code: string, index: number) => {
              return (
                <CodeViewer
                  code={code}
                  language={assetMeta?.language ?? "python"}
                  key={`code-block-${index}`}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </StackContent>
  );
};

export default BreakOut;
